import { useApolloClient, useQuery } from '@apollo/client';
import { DocumentNode } from 'graphql';
import { useCallback, useMemo } from 'react';

const usePlannerQuery = <TData, TVariables>(variables: TVariables, document: DocumentNode) => {
    const client = useApolloClient();

    const writeQuery = useCallback(
        (newData: TData) => {
            client.writeQuery<TData, TVariables>({
                query: document,
                variables,
                data: newData,
            });
        },
        [client, document, variables]
    );

    const readQuery = useCallback(() => {
        try {
            return client.readQuery<TData, TVariables>({
                query: document,
                variables,
            });
        } catch (error) {
            return null;
        }
    }, [client, document, variables]);

    const query = useCallback(
        (vars: TVariables) =>
            client.query<TData, TVariables>({
                variables: vars,
                query: document,
                fetchPolicy: 'network-only',
            }),
        [client, document]
    );

    const result = useQuery<TData, TVariables>(document, { variables, ssr: false });
    const data = useMemo(() => result.data, [result.data]);

    return useMemo(
        () =>
            [
                {
                    data,
                    variables,
                },
                { writeQuery, readQuery, query },
            ] as const,
        [data, query, readQuery, writeQuery, variables]
    );
};

export default usePlannerQuery;
